import React from 'react';
import {
    formatPartnerMoney,
    formatPartnerScheduledAppointment,
    getPartnerStatusLabel,
} from '../../utils/partnerShopApi';
import { getPartnerRequestInitialMessageText, getPartnerRequestTitle } from '../../utils/partnerRequestDisplay';
import ShopReadyReportCard from './ShopReadyReportCard';

const ACTIVE_STATUSES = new Set(['submitted', 'viewed', 'accepted', 'estimate_sent', 'estimate_accepted', 'scheduled', 'in_service', 'ready_for_pickup']);

function getRequestTitle(request) {
    return getPartnerRequestTitle(request, 'Garage repair request');
}

function getStatusClass(status) {
    const normalized = String(status || '').toLowerCase();
    if (['ready_for_pickup', 'picked_up', 'scheduled', 'estimate_accepted'].includes(normalized)) {
        return 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]';
    }
    if (['estimate_sent', 'in_service'].includes(normalized)) {
        return 'border-[#dfd0ff] bg-[#f7f2ff] text-[#6f3cc3]';
    }
    if (['declined', 'failed'].includes(normalized)) {
        return 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]';
    }
    return 'border-[#d9d9d9] bg-white text-[#414141]';
}

function getDeclineReason(request) {
    const notes = String(request?.notes || '').trim();
    if (!notes || /^Customer requested an estimate/i.test(notes)) return '';
    return notes.replace(/^Decline reason:\s*/i, '').trim();
}

function getLastActivity(request) {
    const timeline = Array.isArray(request?.timeline) ? request.timeline : [];
    const lastTimelineItem = timeline
        .filter((item) => item?.created_at)
        .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
    if (lastTimelineItem) return lastTimelineItem;
    if (request?.updated_at) {
        return {
            label: getPartnerStatusLabel(request.request_status),
            created_at: request.updated_at,
        };
    }
    return null;
}

function formatActivityTime(value) {
    if (!value) return '';
    try {
        const date = new Date(value);
        if (!Number.isFinite(date.getTime())) return '';
        return date.toLocaleString();
    } catch (_) {
        return '';
    }
}

function sortRequests(requests = []) {
    return [...requests].sort((a, b) => {
        const activeDiff = Number(ACTIVE_STATUSES.has(String(b?.request_status || '').toLowerCase())) -
            Number(ACTIVE_STATUSES.has(String(a?.request_status || '').toLowerCase()));
        if (activeDiff !== 0) return activeDiff;
        return String(b?.updated_at || b?.created_at || '').localeCompare(String(a?.updated_at || a?.created_at || ''));
    });
}

function hasReachedStatus(currentStatus, status) {
    const order = [
        'submitted',
        'viewed',
        'accepted',
        'estimate_sent',
        'estimate_accepted',
        'scheduled',
        'in_service',
        'ready_for_pickup',
        'picked_up',
        'completed',
    ];
    const currentIndex = order.indexOf(String(currentStatus || '').toLowerCase());
    const targetIndex = order.indexOf(status);
    return currentIndex >= targetIndex && targetIndex >= 0;
}

function findTimelineTime(request, eventName, statusName = '') {
    const timeline = Array.isArray(request?.timeline) ? request.timeline : [];
    const item = timeline.find((entry) => (
        String(entry?.event || '').toLowerCase() === eventName ||
        (statusName && String(entry?.status || '').toLowerCase() === statusName)
    ));
    return item?.created_at || '';
}

function createEvent({
    key,
    title,
    detail = '',
    meta = '',
    createdAt = '',
    tone = 'neutral',
    side = 'shop',
    sequence = 0,
    reportRequest = null,
}) {
    return { key, title, detail, meta, createdAt, tone, side, sequence, reportRequest };
}

function buildRescheduleDetail(request = {}, event = {}) {
    const appointment = formatPartnerScheduledAppointment({
        ...request,
        scheduled_start_at: event.previous_scheduled_start_at || request?.scheduled_start_at || '',
        scheduled_end_at: event.previous_scheduled_end_at || request?.scheduled_end_at || '',
        scheduled_timezone: event.previous_scheduled_timezone || request?.scheduled_timezone || '',
    });
    const reason = String(event.reason || '').trim();
    return [
        appointment ? `Current appointment: ${appointment}` : 'The current appointment needs a new time.',
        reason ? `Reason: ${reason}` : '',
    ].filter(Boolean).join('\n');
}

export function buildGarageProgressEvents(request = {}, options = {}) {
    const { includeMessages = true, includeWaiting = true } = options;
    const status = String(request?.request_status || '').toLowerCase();
    const estimate = request?.latest_estimate || null;
    const appointment = formatPartnerScheduledAppointment(request);
    const shopName = request?.partner_shop?.name || 'Partner shop';
    const events = [];

    const initialDeliveryState = request?.initial_request_delivery_status?.state;
    const submittedIsUnsettled = initialDeliveryState === 'sending' || initialDeliveryState === 'not_queued';
    events.push(createEvent({
        key: 'submitted',
        title: initialDeliveryState === 'failed'
            ? 'Estimate request failed to send'
            : (submittedIsUnsettled ? 'Sending estimate request…' : 'Estimate request sent'),
        detail: getPartnerRequestInitialMessageText(request),
        meta: initialDeliveryState === 'failed' ? 'Failed' : (submittedIsUnsettled ? 'Sending…' : ''),
        createdAt: findTimelineTime(request, 'submitted') || request?.created_at || '',
        tone: initialDeliveryState === 'failed' ? 'danger' : 'driver',
        side: 'driver',
        sequence: 10,
        reportRequest: request,
    }));

    (request?.timeline || [])
        .filter((event) => event?.event === 'handoff')
        .forEach((event, index) => {
            events.push(createEvent({
                key: `handoff-${event.created_at || index}`,
                title: event.label || (event.status === 'success' ? 'Partner handoff succeeded' : 'Partner handoff failed'),
                meta: event.status === 'success' ? '' : 'Failed',
                createdAt: event.created_at || '',
                tone: event.status === 'success' ? 'success' : 'danger',
                sequence: 20 + (index / 100),
            }));
        });

    if (estimate) {
        const estimateDeliveryState = estimate?.delivery_status?.state;
        events.push(createEvent({
            key: 'estimate_sent',
            title: estimateDeliveryState === 'failed'
                ? 'Estimate failed to send'
                : (estimateDeliveryState === 'sending' ? 'Sending estimate…' : 'Estimate sent'),
            detail: estimate.message || 'The shop sent an itemized estimate.',
            meta: formatPartnerMoney(estimate.total_amount, estimate.currency),
            createdAt: estimate.sent_at || findTimelineTime(request, 'estimate_sent') || '',
            tone: estimateDeliveryState === 'failed' ? 'danger' : 'estimate',
            sequence: 30,
        }));
    } else if (includeWaiting && ['submitted', 'viewed', 'accepted'].includes(status)) {
        events.push(createEvent({
            key: 'waiting_estimate',
            title: 'Waiting for shop estimate',
            detail: 'The shop has the report. Estimate updates will appear here.',
            tone: 'waiting',
            sequence: 35,
        }));
    }

    if (status === 'declined' || estimate?.estimate_status === 'declined') {
        const declineReason = getDeclineReason(request);
        events.push(createEvent({
            key: 'declined',
            title: `${shopName} declined this job`,
            detail: declineReason || 'No reason provided.',
            createdAt: status === 'declined' ? request?.updated_at : findTimelineTime(request, 'declined', 'declined'),
            tone: 'danger',
            side: 'shop',
            sequence: 40,
        }));
    } else if (hasReachedStatus(status, 'estimate_accepted') || estimate?.estimate_status === 'accepted') {
        events.push(createEvent({
            key: 'estimate_accepted',
            title: 'Estimate approved',
            detail: 'You approved the shop estimate.',
            createdAt: status === 'estimate_accepted' ? request?.updated_at : findTimelineTime(request, 'estimate_accepted', 'estimate_accepted'),
            tone: 'success',
            side: 'driver',
            sequence: 40,
        }));
    }

    if (request?.scheduled_start_at || hasReachedStatus(status, 'scheduled')) {
        events.push(createEvent({
            key: 'scheduled',
            title: 'Appointment scheduled',
            detail: appointment || 'A drop-off appointment is scheduled.',
            createdAt: status === 'scheduled' ? request?.updated_at : findTimelineTime(request, 'scheduled', 'scheduled'),
            tone: 'success',
            sequence: 50,
        }));
    }

    (request?.timeline || [])
        .filter((event) => (
            event?.event === 'reschedule_requested' &&
            String(event?.status || 'pending').toLowerCase() === 'pending'
        ))
        .forEach((event, index) => {
            events.push(createEvent({
                key: `reschedule_requested-${event.reschedule_request_id || event.created_at || index}`,
                title: `${shopName} requested a reschedule`,
                detail: buildRescheduleDetail(request, event),
                meta: 'Action needed',
                createdAt: event.created_at || request?.updated_at || '',
                tone: 'waiting',
                sequence: 55 + (index / 100),
            }));
        });

    if (hasReachedStatus(status, 'in_service')) {
        events.push(createEvent({
            key: 'in_service',
            title: 'Vehicle in service',
            detail: `${shopName} marked the vehicle in service.`,
            createdAt: status === 'in_service' ? request?.updated_at : findTimelineTime(request, 'in_service', 'in_service'),
            tone: 'estimate',
            sequence: 60,
        }));
    }

    if (hasReachedStatus(status, 'ready_for_pickup')) {
        events.push(createEvent({
            key: 'ready_for_pickup',
            title: 'Ready for pickup',
            detail: `${shopName} marked the vehicle ready for pickup.`,
            createdAt: status === 'ready_for_pickup' ? request?.updated_at : findTimelineTime(request, 'ready_for_pickup', 'ready_for_pickup'),
            tone: 'success',
            sequence: 70,
        }));
    }

    if (status === 'picked_up' || status === 'completed') {
        events.push(createEvent({
            key: 'picked_up',
            title: status === 'completed' ? 'Repair completed' : 'Vehicle picked up',
            detail: status === 'completed' ? 'The shop marked this repair complete.' : 'Pickup was confirmed.',
            createdAt: request?.updated_at || findTimelineTime(request, 'picked_up', 'picked_up'),
            tone: 'success',
            side: 'driver',
            sequence: 80,
        }));
    }

    if (includeMessages) {
        (request?.messages || []).forEach((message, index) => {
            const fromShop = message.sender_role === 'shop';
            // Only driver-sent messages have a real "is this being delivered" question from
            // this viewer's perspective — a shop message already rendered in the chat has,
            // by definition, already reached the driver.
            const messageDeliveryState = fromShop ? null : message?.delivery_status?.state;
            events.push(createEvent({
                key: `message-${message.message_id || index}`,
                title: message.sender_label || (fromShop ? shopName : 'You'),
                detail: message.message_text || '',
                meta: messageDeliveryState === 'failed' ? 'Failed' : (messageDeliveryState === 'sending' ? 'Sending…' : ''),
                createdAt: message.created_at || '',
                tone: messageDeliveryState === 'failed' ? 'danger' : (fromShop ? 'neutral' : 'driver'),
                side: fromShop ? 'shop' : 'driver',
                sequence: 45 + (index / 100),
            }));
        });
    }

    return events.sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : NaN;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : NaN;
        if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) return aTime - bTime;
        return a.sequence - b.sequence;
    });
}

function InlineIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9 5 3h14l2 6" />
            <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
            <path d="M9 22V12h6v10" />
        </svg>
    );
}

export function GarageProgressStrip({ requests = [], onOpenGarageChat }) {
    const visibleRequests = sortRequests(requests).slice(0, 2);
    if (!visibleRequests.length) return null;

    return (
        <div className="mx-auto flex max-w-5xl items-center gap-2 overflow-x-auto">
            <span className="shrink-0 text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">
                Garage
            </span>
            <div className="flex min-w-0 items-center gap-2">
                {visibleRequests.map((request) => {
                    const estimate = request.latest_estimate;
                    const appointment = formatPartnerScheduledAppointment(request);
                    const statusLabel = getPartnerStatusLabel(request.request_status);
                    const detail = appointment || (estimate ? formatPartnerMoney(estimate.total_amount, estimate.currency) : getRequestTitle(request));
                    return (
                        <button
                            key={request.request_id}
                            type="button"
                            onClick={() => onOpenGarageChat?.(request)}
                            className={`inline-flex max-w-[380px] shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-black shadow-sm transition hover:bg-white ${getStatusClass(request.request_status)}`}
                            title={`Message shop: ${request.partner_shop?.name || 'Partner shop'} - ${statusLabel} - ${detail}`}
                        >
                            <InlineIcon />
                            <span className="shrink-0">{statusLabel}</span>
                            <span className="shrink-0 opacity-70">·</span>
                            <span className="min-w-0 truncate">{request.partner_shop?.name || 'Partner shop'}</span>
                            <span className="shrink-0 opacity-70">·</span>
                            <span className="min-w-0 truncate">{detail}</span>
                            <span className="shrink-0 rounded-full bg-white/70 px-2 py-0.5 text-[10px] uppercase tracking-[0.04em]">
                                Open Chat
                            </span>
                        </button>
                    );
                })}
                {requests.length > visibleRequests.length && (
                    <span className="shrink-0 rounded-full border border-[#d9d9d9] bg-white px-3 py-1.5 text-xs font-black text-[#414141]">
                        +{requests.length - visibleRequests.length}
                    </span>
                )}
            </div>
        </div>
    );
}

export function GarageProgressPanel({ requests = [], onOpenGarageChat }) {
    const visibleRequests = sortRequests(requests);
    if (!visibleRequests.length) return null;

    return (
        <div className="rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] p-3.5">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.09em] text-[#D81F26]">Garage Progress</p>
                    <p className="mt-1 text-[13px] font-semibold text-[#414141]">Shop updates for this diagnostic ticket.</p>
                </div>
                <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[10px] font-black text-[#414141]">
                    {visibleRequests.length}
                </span>
            </div>
            <div className="mt-3 space-y-2.5">
                {visibleRequests.map((request) => {
                    const estimate = request.latest_estimate;
                    const appointment = formatPartnerScheduledAppointment(request);
                    const activity = getLastActivity(request);
                    const declineReason = getDeclineReason(request);
                    return (
                        <div key={request.request_id} className="rounded-xl border border-[#d9d9d9] bg-white p-3">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.06em] ${getStatusClass(request.request_status)}`}>
                                            {getPartnerStatusLabel(request.request_status)}
                                        </span>
                                        <span className="text-[11px] font-bold text-[#414141]">{request.partner_shop?.name || 'Partner shop'}</span>
                                    </div>
                                    <p className="mt-1.5 truncate text-sm font-black text-[#414141]">{getRequestTitle(request)}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onOpenGarageChat?.(request)}
                                    className="inline-flex min-h-[34px] shrink-0 items-center justify-center rounded-xl bg-[#414141] px-3 text-xs font-black text-white transition hover:bg-[#2A2A2A]"
                                >
                                    Message shop
                                </button>
                            </div>
                            <div className="mt-3 grid gap-2 text-xs font-black text-[#414141] sm:grid-cols-2">
                                <span className="rounded-xl bg-[#F9F9F9] px-3 py-2">
                                    Estimate: {estimate ? formatPartnerMoney(estimate.total_amount, estimate.currency) : 'Waiting'}
                                </span>
                                <span className="rounded-xl bg-[#F9F9F9] px-3 py-2">
                                    Appointment: {appointment || 'Not scheduled'}
                                </span>
                            </div>
                            {request.request_status === 'declined' && (
                                <p className="mt-2 rounded-xl border border-[#ffd2d2] bg-[#fff1f1] px-3 py-2 text-xs font-bold text-[#b42318]">
                                    Decline note: {declineReason || 'No reason provided.'}
                                </p>
                            )}
                            {activity && (
                                <p className="mt-2 text-xs font-semibold text-[#414141]">
                                    Last activity: {activity.label || getPartnerStatusLabel(request.request_status)}
                                    {activity.created_at ? ` - ${formatActivityTime(activity.created_at)}` : ''}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function getEventClass(event) {
    if (event.tone === 'success') return 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]';
    if (event.tone === 'estimate') return 'border-[#dfd0ff] bg-[#f7f2ff] text-[#6f3cc3]';
    if (event.tone === 'waiting') return 'border-[#f7d58f] bg-[#fff8e8] text-[#936000]';
    if (event.tone === 'danger') return 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]';
    if (event.tone === 'info') return 'border-[#d9d9d9] bg-[#F9F9F9] text-[#414141]';
    if (event.tone === 'driver') return 'border-[#414141] bg-[#414141] text-white';
    return 'border-[#d9d9d9] bg-white text-[#414141]';
}

function GarageEventCard({ event }) {
    const isDriverSide = event.side === 'driver';
    const isDriverBubble = event.tone === 'driver';
    return (
        <div className={`flex ${isDriverSide ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[92%]">
                <div className={`rounded-2xl border px-3.5 py-3 shadow-sm ${getEventClass(event)}`}>
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[12px] font-black leading-tight">{event.title}</p>
                        {event.meta && (
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${isDriverBubble ? 'bg-white/20 text-white' : 'bg-white/80 text-current'}`}>
                                {event.meta}
                            </span>
                        )}
                    </div>
                    {event.detail && (
                        <p className={`mt-1 whitespace-pre-wrap text-[13px] font-semibold leading-relaxed ${isDriverBubble ? 'text-white/85' : 'text-[#414141]'}`}>
                            {event.detail}
                        </p>
                    )}
                    {event.createdAt && (
                        <p className={`mt-1.5 text-[10px] font-bold ${isDriverBubble ? 'text-white/60' : 'text-[#9e9e9e]'}`}>
                            {formatActivityTime(event.createdAt)}
                        </p>
                    )}
                </div>
                {event.reportRequest && (
                    <ShopReadyReportCard
                        request={event.reportRequest}
                        className="mt-2"
                    />
                )}
            </div>
        </div>
    );
}

export function GarageProgressConversation({
    requests = [],
    onOpenGarageChat,
    includeMessages = true,
    includeWaiting = true,
    showRequestHeader = true,
    openLabel = 'Message shop',
}) {
    const visibleRequests = sortRequests(requests);
    if (!visibleRequests.length) return null;

    return (
        <div className="w-full max-w-[560px] space-y-4">
            {visibleRequests.map((request) => {
                const events = buildGarageProgressEvents(request, { includeMessages, includeWaiting });
                return (
                    <section key={request.request_id} className="space-y-2.5">
                        {showRequestHeader && (
                            <div className="rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] px-3.5 py-3 shadow-sm">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-black uppercase tracking-[0.09em] text-[#D81F26]">Garage updates</p>
                                        <p className="mt-1 truncate text-sm font-black text-[#414141]">{request.partner_shop?.name || 'Partner shop'}</p>
                                        <p className="mt-0.5 line-clamp-2 text-[12px] font-semibold leading-snug text-[#414141]">{getRequestTitle(request)}</p>
                                    </div>
                                    {onOpenGarageChat && (
                                        <button
                                            type="button"
                                            onClick={() => onOpenGarageChat?.(request)}
                                            className="inline-flex min-h-[34px] shrink-0 items-center justify-center rounded-xl bg-[#414141] px-3 text-xs font-black text-white transition hover:bg-[#2A2A2A]"
                                        >
                                            {openLabel}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="space-y-2">
                            {events.map((event) => (
                                <GarageEventCard key={`${request.request_id}-${event.key}`} event={event} />
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
